/**
 * b2b-plus-bulk-add.js
 *
 * Defines two custom elements that together implement "Quick Add" (bulk add)
 * on collection pages.
 *
 * ┌─────────────────────────────────────────────────────────────┐
 * │  <b2b-plus-bulk-add-trigger>   — one per product card       │
 * │    Wraps a checkbox and holds the product's variants JSON.   │
 * │    Exposes checked / variants / handle / productId getters.  │
 * ├─────────────────────────────────────────────────────────────┤
 * │  <b2b-plus-bulk-add-action>    — one per collection page     │
 * │    Contains the "Quick Add" button.                          │
 * │    On click: collects all checked triggers, resolves         │
 * │    inventory, and writes the first available line item per   │
 * │    product into IndexedDB.                                   │
 * └─────────────────────────────────────────────────────────────┘
 */

import {
  getInventory,
} from 'b2b-plus/inventory';
import {
  toastNotification,
  setB2bCtaProgress,
} from 'b2b-plus/ui';
import {
  addFirstAvailableB2BLine,
  productHandleInCart,
} from 'b2b-plus/utils';

// ── Trigger element ───────────────────────────────────────────────────────────


/**
 * `<b2b-plus-bulk-add-trigger>`
 *
 * Place one of these inside every product card that should participate in
 * bulk-add.  It must contain:
 *   - A checkbox with `data-bulk-add-checkbox-input`
 *   - A `<script type="application/json" data-variants-json>` tag with the
 *     product's variants array serialised as JSON
 *
 * Required attributes:
 *   - `data-handle`     — Shopify product handle
 *   - `data-product-id` — Shopify product ID (numeric string)
 */
class B2BPlusBulkAddTrigger extends HTMLElement {

  /**
   * Whether the user has ticked the checkbox inside this trigger.
   * @type {boolean}
   */
  get checked() {
    return this.querySelector('[data-bulk-add-checkbox-input]')?.checked ?? false;
  }

  /**
   * The parsed variants array from the embedded `<script data-variants-json>`
   * tag.  Returns an empty array when the tag is absent or the JSON is
   * malformed.
   * @type {Array<object>}
   */
  get variants() {
    const scriptTag = this.querySelector('script[data-variants-json]');
    if (!scriptTag) return [];
    try {
      return JSON.parse(scriptTag.textContent) ?? [];
    } catch {
      return [];
    }
  }

  /**
   * The product's option definitions (e.g. `["Color", "Size"]`), parsed from the
   * sibling `<script data-b2b-plus-product-json>` the card already embeds. Needed
   * so bulk-add can detect the split option and add only its first value (mirroring
   * quick-add and the product page). Returns `[]` when the script is absent/invalid.
   * @type {Array<string|object>}
   */
  get options() {
    const scriptTag = this.parentElement?.querySelector('script[data-b2b-plus-product-json]');
    if (!scriptTag?.textContent) return [];
    try {
      return JSON.parse(scriptTag.textContent)?.options ?? [];
    } catch {
      return [];
    }
  }

  /**
   * The Shopify product handle supplied via `data-handle`.
   * @type {string}
   */
  get handle() {
    return this.dataset.handle ?? '';
  }

  /**
   * The Shopify numeric product ID supplied via `data-product-id`.
   * Used as the primary identifier when fetching inventory so the request
   * does not rely on product IDs embedded inside the variants JSON (which
   * may be absent or unreliable in some storefronts).
   * @type {string}
   */
  get productId() {
    return this.dataset.productId ?? '';
  }
}

customElements.define('b2b-plus-bulk-add-trigger', B2BPlusBulkAddTrigger);

// ── Select-all checkbox element ───────────────────────────────────────────────

/**
 * `<b2b-plus-checkbox-trigger>`
 *
 * Renders a "Select All" checkbox that:
 *   - Checks / unchecks every `[data-bulk-add-checkbox-input]` on the page.
 *   - Reflects the collective state: checked when all are selected, indeterminate
 *     when some are selected, unchecked when none are selected.
 *   - Stays in sync when individual checkboxes are changed by the user or
 *     cleared programmatically (via the custom `b2b-plus:checkboxes-cleared`
 *     event dispatched by `B2BPlusBulkAddAction`).
 */
class B2BPlusCheckboxTrigger extends HTMLElement {
  /** @type {((e: Event) => void) | null} */
  #onDocChange       = null;
  /** @type {((e: Event) => void) | null} */
  #onCleared         = null;
  /** @type {(() => void) | null} */
  #onSelectAllChange = null;

  connectedCallback() {
    const selectAll = this.querySelector('[data-select-all-checkbox]');
    if (!selectAll) return;

    this.#onSelectAllChange = () => {
      const checked = selectAll.checked;
      document.querySelectorAll('[data-bulk-add-checkbox-input]').forEach((cb) => {
        cb.checked = checked;
      });
      this.#sync();
    };
    selectAll.addEventListener('change', this.#onSelectAllChange);

    this.#onDocChange = (e) => {
      if (e.target.matches('[data-bulk-add-checkbox-input]')) this.#sync();
    };
    this.#onCleared = () => this.#sync();

    document.addEventListener('change', this.#onDocChange);
    document.addEventListener('b2b-plus:checkboxes-cleared', this.#onCleared);

    // If the user clicked "Select All" before this module finished loading
    // (the checkbox is a native input and toggles visually before upgrade),
    // apply the checked state to all product checkboxes now so the intent
    // is not silently lost when #sync() re-evaluates below.
    if (selectAll.checked) {
      document.querySelectorAll('[data-bulk-add-checkbox-input]').forEach((cb) => {
        cb.checked = true;
      });
    }

    this.#sync();
  }

  disconnectedCallback() {
    const selectAll = this.querySelector('[data-select-all-checkbox]');
    if (selectAll && this.#onSelectAllChange) {
      selectAll.removeEventListener('change', this.#onSelectAllChange);
    }
    if (this.#onDocChange) document.removeEventListener('change', this.#onDocChange);
    if (this.#onCleared)   document.removeEventListener('b2b-plus:checkboxes-cleared', this.#onCleared);
  }

  #sync() {
    const selectAll = this.querySelector('[data-select-all-checkbox]');
    if (!selectAll) return;
    const all     = [...document.querySelectorAll('[data-bulk-add-checkbox-input]')];
    const total   = all.length;
    const checked = all.filter((cb) => cb.checked).length;
    selectAll.checked       = total > 0 && checked === total;
    selectAll.indeterminate = checked > 0 && checked < total;

    const countEl = this.querySelector('[data-selected-count]');
    if (countEl) {
      if (checked > 0) {
        countEl.textContent = `${checked} selected`;
        countEl.hidden = false;
      } else {
        countEl.hidden = true;
      }
    }
  }
}

customElements.define('b2b-plus-checkbox-trigger', B2BPlusCheckboxTrigger);

// ── Action element ────────────────────────────────────────────────────────────


/**
 * `<b2b-plus-bulk-add-action>`
 *
 * Place one of these on the collection page (e.g. above/below the product
 * grid).  It must contain a `<button>` that the user clicks to trigger the
 * bulk-add operation.
 *
 * On click the element:
 *   1. Collects every checked `<b2b-plus-bulk-add-trigger>` on the page.
 *   2. Fetches batched inventory for all selected products in a single API
 *      call, preferring the explicit `data-product-id` over the variant's
 *      embedded `product_id`.
 *   3. For each product not already in the B2B cart, adds an empty combination shell
 *      (no quantities) via `addFirstAvailableB2BLine`, same resolution as quick-add.
 *   4. Shows a success/info/error toast and clears checkboxes for products that were added.
 */
class B2BPlusBulkAddAction extends HTMLElement {

  /** @type {boolean} Guards against overlapping bulk-add runs (double-click / repeated trigger). */
  #busy = false;

  // ── Lifecycle ──────────────────────────────────────────────────────────

  /**
   * Called by the browser when the element is inserted into the DOM.
   * Attaches a delegated click listener on the element so the handler works
   * even if the inner button is rendered after upgrade.
   */
  connectedCallback() {
    this.addEventListener('click', (clickEvent) => {
      if (clickEvent.target.closest('button')) {
        this.#runBulkAdd();
      }
    });
  }

  // ── Private methods ───────────────────────────────────────────────────

  /**
   * Main entry point for the bulk-add operation.  Orchestrates validation,
   * inventory resolution, cart state reading, and IndexedDB writes.
   *
   * @returns {Promise<void>}
   */
  async #runBulkAdd() {
    // Reentrancy guard: a second trigger while a run is in flight would issue
    // duplicate inventory reads / IDB writes and emit confusing toasts.
    if (this.#busy) return;
    this.#busy = true;

    const { strings: b2bStrings } = window.b2bPlus;

    const actionButton = this.querySelector('button');

    // ── 1. Collect checked triggers ──────────────────────────────────────
    const checkedTriggers = [...document.querySelectorAll('b2b-plus-bulk-add-trigger')]
      .filter((trigger) => trigger.checked);

    if (!checkedTriggers.length) {
      toastNotification(b2bStrings.bulkAddNoneSelected, 'error');
      this.#busy = false;
      return;
    }

    this.#clearStaleError();
    if (actionButton) {
      actionButton.disabled = true;
      setB2bCtaProgress(actionButton, 'busy', b2bStrings.cartCheckoutProcessing);
    }

    try {
      // ── 2. Validate that each trigger has usable variant data ────────────
      const productEntries = checkedTriggers
        .map((trigger) => ({
          trigger,
          productHandle: trigger.handle,
          productId:     trigger.productId,
          variants:      trigger.variants,
          options:       trigger.options,
        }))
        .filter(({ productHandle, variants }) => productHandle && variants.length > 0);

      if (!productEntries.length) {
        toastNotification(b2bStrings.bulkAddNoVariants, 'error');
        return;
      }

      // ── 3. Fetch inventory for all selected products in one request ───────
      // Prefer the explicit data-product-id on the trigger element; fall back
      // to product_id embedded inside the variant JSON objects.
      const uniqueProductIds = [...new Set(
        productEntries.map(({ productId, variants }) =>
          productId
            || variants.map((variant) => variant.product_id).find(Boolean)
            || null,
        ).filter(Boolean),
      )];

      // Wrap getInventory in its own try/catch so network-level failures
      // (fetch throws: offline, timeout, DNS error, JSON parse error) show
      // the specific inventory-unavailable toast rather than the generic
      // bulkAddError that the outer catch uses for all other failures.
      let inventoryResponse;
      try {
        inventoryResponse = await getInventory(uniqueProductIds);
      } catch (networkErr) {
        console.error('b2b-plus: getInventory request failed', networkErr);
        toastNotification(b2bStrings.bulkAddInventoryError, 'error');
        return;
      }
      if (!inventoryResponse?.success || !inventoryResponse?.data) {
        toastNotification(b2bStrings.bulkAddInventoryError, 'error');
        return;
      }

      const invData = inventoryResponse.data;

      /** Handles where no inventory shell could be created. */
      const handlesWithNoInventory = [];
      /** @type {Set<string>} */
      const addedHandles = new Set();
      /** @type {Set<string>} */
      const newlyWritten = new Set();

      for (const { productHandle, productId, variants, options } of productEntries) {
        if (await productHandleInCart(productHandle)) {
          addedHandles.add(productHandle);
          continue;
        }

        const pid = productId || variants.map((v) => v.product_id).find(Boolean);
        const numericId = Number(pid);
        if (!numericId) {
          handlesWithNoInventory.push(productHandle);
          continue;
        }

        const productJSON = { handle: productHandle, id: numericId, variants, options };
        const outcome     = await addFirstAvailableB2BLine(productJSON, invData);

        if (outcome.ok) {
          addedHandles.add(productHandle);
          newlyWritten.add(productHandle);
        } else {
          handlesWithNoInventory.push(productHandle);
        }
      }

      const uncheckByHandles = (handleSet) => {
        checkedTriggers.forEach((trigger) => {
          if (!handleSet.has(trigger.handle)) return;
          const checkbox = trigger.querySelector('[data-bulk-add-checkbox-input]');
          if (checkbox) checkbox.checked = false;
        });
        document.dispatchEvent(new CustomEvent('b2b-plus:checkboxes-cleared'));
      };

      const failedHandleSet = new Set(handlesWithNoInventory);

      // ── 5. Nothing new written ─────────────────────────────────────────────
      if (!newlyWritten.size) {
        if (handlesWithNoInventory.length) {
          uncheckByHandles(failedHandleSet);
          toastNotification(
            this.#buildResultMessage(0, handlesWithNoInventory, b2bStrings),
            'error',
          );
        } else if (addedHandles.size) {
          toastNotification(b2bStrings.quickAddAlreadyInCart, 'info', 2000);
          uncheckByHandles(addedHandles);
        } else {
          toastNotification(b2bStrings.bulkAddError, 'error');
        }
        return;
      }

      uncheckByHandles(new Set([...addedHandles, ...failedHandleSet]));

      // ── 6. Show result toast and stay on the page ──────────────────────────
      const addedCount         = newlyWritten.size;
      const hasPartialWarnings = handlesWithNoInventory.length > 0;
      const toastType = hasPartialWarnings ? 'info' : 'success';
      const toastMessage = hasPartialWarnings
        ? this.#buildResultMessage(addedCount, handlesWithNoInventory, b2bStrings)
        : b2bStrings.bulkAddPartialAdded.replace('{count}', String(addedCount));

      toastNotification(toastMessage, toastType, 2000);

    } catch (unexpectedError) {
      console.error('b2b-plus: bulk-add failed', unexpectedError);
      toastNotification(b2bStrings.bulkAddError, 'error');

    } finally {
      this.#busy = false;
      if (actionButton) {
        actionButton.disabled = false;
        setB2bCtaProgress(actionButton, 'idle');
      }
    }
  }

  /**
   * Plain-text message for the toast (newlines between lines; `toastNotification` uses pre-line).
   *
   * @param {number}   addedCount         - Number of line items successfully written to the cart.
   * @param {string[]} handlesNoInventory - Product handles for which no variant could be added.
   * @param {object}   b2bStrings         - `window.b2bPlus.strings` reference.
   * @returns {string}
   */
  #buildResultMessage(addedCount, handlesNoInventory, b2bStrings) {
    const lines = [];

    if (addedCount > 0) {
      lines.push(b2bStrings.bulkAddPartialAdded.replace('{count}', String(addedCount)));
    }

    if (handlesNoInventory.length) {
      for (const handle of handlesNoInventory) {
        lines.push(`${handle} — ${b2bStrings.bulkAddNoInventory}`);
      }
    }

    return lines.join('\n') || b2bStrings.bulkAddError;
  }

  /**
   * Hides an inline `data-bulk-add-error` node immediately before this element when
   * the theme leaves one in the DOM; new feedback uses toasts only.
   */
  #clearStaleError() {
    const previousSibling = this.previousElementSibling;
    if (previousSibling?.hasAttribute('data-bulk-add-error')) {
      previousSibling.hidden = true;
    }
  }
}

customElements.define('b2b-plus-bulk-add-action', B2BPlusBulkAddAction);
